import React, {useReducer} from 'react';
import {ThemeMode} from '../../enums/theme-mode';
import {AppConfig} from '../../interfaces/app-config';
import {
  /* bluebirdDarkTheme, */ bluebirdLightTheme,
  bluebirdThemeSettings,
} from '../../theme/bluebird';
import {Logger} from '../../utils/managers/logger';
import {AppContext} from './context';
import {AppContextActionType, AppContextState} from './types';
import {Theme} from '@mui/material/styles';
import {useEffect} from 'react';
import {getApps, getApp, initializeApp} from 'firebase/app';
import {BackendApiManager} from '../../managers/backend-api';
import {ThemeSettings} from '../../interfaces/theme-settings';
import {ThemeOption} from '../../enums/theme-option';
import {LocalStorageKey} from '../../enums/local-storage-key';
import reducer from './reducer';
import {consumeSocialAppLaunchParams} from '../../utils/social-app/social-app-launch';

// Logger
const logger: Logger = new Logger('AppContext');

// Load Config
const appConfig: AppConfig = require('../../config.json');

// Initial Methods
const getCurrentTheme = (_themeMode: ThemeMode): Theme => {
  // TODO: Uncomment this below after dark mode is fully adapted
  /* // Hold result
    let currentTheme: Theme = bluebirdLightTheme;

    // Assume Bluebird by default because we only have one product at the moment
    // Theme Mode
    if (themeMode === ThemeMode.Dark) {
        // Assume Dark
        currentTheme = bluebirdDarkTheme;
    } else {
        // Assume Light by default
        currentTheme = bluebirdLightTheme;
    }

    // Done
    return currentTheme; */

  // Supressing theme switch for now
  return bluebirdLightTheme;
};
const getCurrentThemeSettings = (): ThemeSettings => {
  // Hold result
  let currentThemeSettings: ThemeSettings = bluebirdThemeSettings;

  // Assume Bluebird by default because we only have one product at the moment
  currentThemeSettings = bluebirdThemeSettings;

  // Done
  return currentThemeSettings;
};

// Check current theme from system
let currentThemeMode: ThemeMode = getCurrentThemeSettings().defaultThemeMode;

// TODO: Uncomment this below after dark mode is fully adapted
// https://stackoverflow.com/a/57795495/13157395
/* if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    // Go dark mode
    currentThemeMode = ThemeMode.Dark;
} else {
    // Go Light
    currentThemeMode = ThemeMode.Light;
} */

// TODO: Uncomment this below after dark mode is fully adapted
// Override it with local storage key
//const userPreferredThemeMode: string | null = localStorage.getItem(LocalStorageKey.ThemeMode);
// If the local storage key is valid, override the current them mode. Otherwise, use the system theme.
/* if (userPreferredThemeMode === ThemeMode.Dark || userPreferredThemeMode === ThemeMode.Light) {
    currentThemeMode = userPreferredThemeMode;
} */

// Set Initial State
const initialState: AppContextState = {
  firebaseApp: !getApps().length ? initializeApp(require('../../firebase.json')) : getApp(),
  configuration: appConfig,
  currentTheme: getCurrentTheme(currentThemeMode),
  currentThemeMode: currentThemeMode,
  currentThemeSettings: getCurrentThemeSettings(),
  runningFromSocialApp: consumeSocialAppLaunchParams(),
};

export const AppContextProvider = (props: {children: React.ReactNode}) => {
  // Create Reducer
  const [state, dispatch] = useReducer(reducer, initialState);

  // Component mount
  useEffect(() => {
    // Log
    logger.debug('App initialized');

    // Watch for system dark mode changes
    // https://stackoverflow.com/a/57795495/13157395
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      // Get local storage theme
      const localStorageTheme: string | null = localStorage.getItem(LocalStorageKey.ThemeMode);

      // Only change the theme with the listener if the user did not set their theme manually
      if (localStorageTheme !== ThemeMode.Dark && localStorageTheme !== ThemeMode.Light) {
        // Calculate new theme mode
        const newThemeMode: ThemeMode = e.matches ? ThemeMode.Dark : ThemeMode.Light;

        // Dispatch change
        dispatch({
          type: AppContextActionType.CHANGE_THEME_MODE,
          theme: getCurrentTheme(newThemeMode),
          themeMode: newThemeMode,
        });
      }
    });
  }, []);

  // Configure backend managers
  const init = async (): Promise<any> => {
    // Re-hook Backend App Config
    await BackendApiManager.instance.hookAppConfig(state.configuration);
  };

  // Change theme mode
  const changeThemeMode = (option: ThemeOption) => {
    // Theme mode to use
    let themeMode: ThemeMode = ThemeMode.Light;
    if (option === ThemeOption.Dark) {
      // Set theme mode
      themeMode = ThemeMode.Dark;

      // Set local storage
      localStorage.setItem(LocalStorageKey.ThemeMode, ThemeMode.Dark);
    } else if (option === ThemeOption.Light) {
      // Set theme mode
      themeMode = ThemeMode.Light;

      // Set local storage
      localStorage.setItem(LocalStorageKey.ThemeMode, ThemeMode.Light);
    } else {
      // Remove from local storage
      localStorage.removeItem(LocalStorageKey.ThemeMode);

      // Find system theme
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        // Dark
        themeMode = ThemeMode.Dark;
      } else {
        // Light
        themeMode = ThemeMode.Light;
      }
    }

    // Dispatch change
    dispatch({
      type: AppContextActionType.CHANGE_THEME_MODE,
      theme: getCurrentTheme(themeMode),
      themeMode: themeMode,
    });
  };

  return (
    <AppContext.Provider
      value={{
        state,
        // Methods
        init,
        changeThemeMode,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};
